import React, {useLayoutEffect, useRef, useState} from 'react';
import {TooltipBubble, TooltipWrapper} from './styled';
import {createPortal} from 'react-dom';

interface CustomTooltipProps {
    title: React.ReactNode;
    children: React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    style?: React.CSSProperties;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
	title,
	children,
	placement = 'top',
	style,
}) => {
	const [visible, setVisible] = useState(false);
	const timeoutRef = useRef<number | null>(null);
	const [coords, setCoords] = useState<{top: number; left: number}>({top: 0, left: 0});
	const wrapperRef = useRef<HTMLSpanElement>(null);

	const showTooltip = () => {
		window.clearTimeout(timeoutRef.current ?? undefined);
		setVisible(true);
	};

	const hideTooltip = () => {
		window.clearTimeout(timeoutRef.current ?? undefined);
		setVisible(false);
	};

	useLayoutEffect(() => {
		if (visible && wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			let top = rect.top;
			let left = rect.left + rect.width / 2;
			if (placement === 'top') {
				top = rect.top - 8;
				left = rect.left + rect.width / 2;
			}
			if (placement === 'bottom') {
				top = rect.bottom + 8;
				left = rect.left + rect.width / 2;
			}
			if (placement === 'left') {
				top = rect.top + rect.height / 2;
				left = rect.left - 8;
			}
			if (placement === 'right') {
				top = rect.top + rect.height / 2;
				left = rect.right + 8;
			}
			if (placement === 'center') {
				top = window.innerHeight / 2;
				left = window.innerWidth / 2;
			}
			setCoords({top, left});
		}
	}, [visible, placement]);

	return (
		<>
			<TooltipWrapper
				ref={wrapperRef}
				onMouseEnter={showTooltip}
				onMouseLeave={hideTooltip}
				onFocus={showTooltip}
				onBlur={hideTooltip}
				tabIndex={0}
				style={style}
			>
				{children}
			</TooltipWrapper>
			{visible &&
                createPortal(
                	<TooltipBubble
                		$placement={placement}
                		className="visible"
                		role="tooltip"
                		style={{
                			position: 'fixed',
                			top: coords.top,
                			left: coords.left,
                			transform:
                                placement === 'top'
                                	? 'translate(-50%, -100%)'
                                	: placement === 'bottom'
                                		? 'translate(-50%, 0)'
                                		: placement === 'left'
                                			? 'translate(-100%, -50%)'
                                			: placement === 'right'
                                				? 'translate(0, -50%)'
                                				: 'translate(-50%, -50%)',
                			zIndex: 99999,
                		}}
                	>
                		{title}
                	</TooltipBubble>,
                	document.body
                )
			}
		</>
	);
};

export default CustomTooltip;